/* eslint-disable max-lines */
import { Col, Row } from 'antd';
import i18next, { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Card } from 'src/components/Card/Card';
import { DataView } from 'src/components/DataView/DataView';
import { ResourceNameView } from 'src/components/ResourceNameView/ResourceNameView';
import { LoanApplicationDto } from 'src/generated-api-client';
import { ApplicationsStore } from 'src/stores/ApplicationsStore/ApplicationsStore';
import {
    ApplicationStatus,
    OfferInstallmentOption,
} from 'src/stores/ApplicationsStore/ApplicationsStore.types';
import { InstallmentOptionResourceStore } from 'src/stores/Resources/InstallmentOptionResource';
import { PaymentFreequencyResourceStore } from 'src/stores/Resources/PaymentFreequencyResourceStore';
import styled from 'styled-components';

export const ApplicationDetailsGeneralInformationTab = observer(() => {
    const application =
        ApplicationsStore.currentItem as LoanApplicationDto | null;

    return (
        <>
            <GeneralInformationCard application={application} />
            <OfferDetailsCard application={application} />
        </>
    );
});

type InfoCardProps = {
    application: LoanApplicationDto | null;
};
const GeneralInformationCard = ({ application }: InfoCardProps) => {
    return (
        <Card
            title={t(
                'Page.ApplicationDetailsPage.GeneralInformation',
                'General information',
            )}
        >
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                    <DataView
                        label={t(
                            'Page.ApplicationDetailsPage.ApplicationID',
                            'Application ID',
                        )}
                        value={application?.number}
                    />
                </Col>
                <Col xs={24} sm={12}>
                    <DataView
                        label={t(
                            'Page.ApplicationDetailsPage.CreatedAt',
                            'Creation date',
                        )}
                        value={application?.createdAt}
                        formatValue="dateTimeSeconds"
                    />
                </Col>
            </Row>
        </Card>
    );
};

const OfferDetailsCard = ({ application }: InfoCardProps) => {
    const showFinalOfferDetails = [
        ApplicationStatus.ACTIVE,
        ApplicationStatus.FULLY_PAID,
    ].includes(application?.status as ApplicationStatus);
    const showInitialOfferDetails = !showFinalOfferDetails;

    return (
        <Card
            title={t(
                'Page.ApplicationDetailsPage.OfferDetails.Title',
                'Offer details',
            )}
        >
            {showInitialOfferDetails && (
                <InitialOfferDetailsInfo application={application} />
            )}
            {showFinalOfferDetails && (
                <FinalOfferDetailsInfo application={application} />
            )}
        </Card>
    );
};

const InitialOfferDetailsInfo = ({ application }: InfoCardProps) => {
    return (
        <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.InitialOfferDetails.OfferName',
                        'Offer name',
                    )}
                    value={application?.initialOffer?.name}
                />
            </Col>
            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.InitialOfferDetails.Partner',
                        'Partner',
                    )}
                    value={
                        application?.assignedPartner?.arabicName ??
                        application?.assignedPartner?.englishName
                    }
                />
            </Col>

            <Col xs={24} sm={24}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.InitialOfferDetails.Conditions',
                        'Conditions',
                    )}
                    value={getOfferCardCondition(application?.initialOffer)}
                    formatValue={(value: string) => (
                        <StyledCondition>{value}</StyledCondition>
                    )}
                />
            </Col>
        </Row>
    );
};

const getOfferCardCondition = (offer: LoanApplicationDto['initialOffer']) => {
    if (i18next.language === 'ar' && offer?.cardArabic) {
        return offer?.cardArabic;
    }

    return offer?.cardEnglish;
};

const FinalOfferDetailsInfo = ({ application }: InfoCardProps) => {
    return (
        <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.InitialOfferDetails.OfferName',
                        'Offer name',
                    )}
                    value={application?.finalOffer?.name}
                />
            </Col>
            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.InitialOfferDetails.Partner',
                        'Partner',
                    )}
                    value={
                        application?.assignedPartner?.arabicName ??
                        application?.assignedPartner?.englishName
                    }
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.FinancingAmount',
                        'Financing amount ',
                    )}
                    value={application?.finalOffer?.financingAmount}
                    formatValue="currency-sar"
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.FirstPaymentDate',
                        'First payment date',
                    )}
                    value={application?.finalOffer?.firstPaymentDate}
                    formatValue="date"
                />
            </Col>
            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.FinalSettlementDate',
                        'Final settlement date',
                    )}
                    value={application?.finalOffer?.finalSettlementDate}
                    formatValue="date"
                />
            </Col>

            <Col xs={24} sm={12}>
                <ResourceNameView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.PaymentFrequency',
                        'Payment frequency',
                    )}
                    resourceStore={PaymentFreequencyResourceStore}
                    value={application?.finalOffer?.paymentFrequency}
                />
            </Col>
            <Col xs={24} sm={12}>
                <ResourceNameView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.InstallmentOption',
                        'Installment option',
                    )}
                    resourceStore={InstallmentOptionResourceStore}
                    value={application?.finalOffer?.installmentOption}
                />
            </Col>

            {application?.finalOffer?.installmentOption ===
                OfferInstallmentOption.FIXED_VALUE && (
                <Col xs={24} sm={12}>
                    <DataView
                        label={t(
                            'Page.ApplicationDetailsPage.FinalOfferDetails.InstallmentAmount',
                            'Installment amount',
                        )}
                        value={application?.finalOffer?.installmentAmount}
                        formatValue="currency-sar"
                    />
                </Col>
            )}

            {application?.finalOffer?.installmentOption ===
                OfferInstallmentOption.PERCENTAGE && (
                <Col xs={24} sm={12}>
                    <DataView
                        label={t(
                            'Page.ApplicationDetailsPage.FinalOfferDetails.DeductionRate',
                            'Deduction rate',
                        )}
                        value={application?.finalOffer?.deductionRatePct}
                        formatValue="percent"
                    />
                </Col>
            )}

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.HoldbackRate',
                        'Holdback rate',
                    )}
                    value={application?.finalOffer?.holdbackRatePct}
                    formatValue="percent"
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.MinimumInstallmentAmount',
                        'Minimum installment amount',
                    )}
                    value={application?.finalOffer?.minInstallmentAmount}
                    formatValue="currency-sar"
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.MaximumInstallmentAmount',
                        'Maximum installment amount',
                    )}
                    value={application?.finalOffer?.maxInstallmentAmount}
                    formatValue="currency-sar"
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.FinancingTenor',
                        'Financing tenor (Months)',
                    )}
                    value={application?.finalOffer?.term}
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.MurabahaRate',
                        'Murabaha rate  ',
                    )}
                    value={application?.finalOffer?.murabahaRatePct}
                    formatValue="percent"
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.TotalMurabahaAmount',
                        'Total Murabaha amount',
                    )}
                    value={application?.finalOffer?.totalMurabahaAmount}
                    formatValue="currency-sar"
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.TotalRepayableAmount',
                        'Total repayable amount',
                    )}
                    value={application?.finalOffer?.totalRepayableAmount}
                    formatValue="currency-sar"
                    help={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.TotalRepayableAmountHelp',
                        'The total amount that will be collected over time to fully repay your loan.',
                    )}
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.DisbursementAmount',
                        'Disbursement amount',
                    )}
                    value={application?.finalOffer?.disbursementAmount}
                    formatValue="currency-sar"
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.AdminFeeAmount',
                        'Admin fee amount ',
                    )}
                    value={application?.finalOffer?.adminFeeAmount}
                    formatValue="currency-sar"
                />
            </Col>

            <Col xs={24} sm={12}>
                <DataView
                    label={t(
                        'Page.ApplicationDetailsPage.FinalOfferDetails.AdminFeeRateWithVAT',
                        'Admin fee rate with VAT',
                    )}
                    value={application?.finalOffer?.adminFeeWithVatAmount}
                    formatValue="currency-sar"
                />
            </Col>
        </Row>
    );
};

const StyledCondition = styled.div`
    white-space: pre-wrap;
`;
